import { useCallback, useEffect, useRef } from 'react';
import { Animated, PanResponder, Platform, type LayoutChangeEvent } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

import { DURATION } from '@/constants/animation';

/** What triggered a dismissal: the gesture itself, or an equivalent offered to assistive tech. */
export type SwipeDismissSource = 'gesture' | 'action';

/** The part of a release a dismissal decision depends on. */
export type SwipeRelease = {
  dy: number;
  vy: number;
};

/** Upward drag distance (px) past which a release dismisses rather than springs back. */
const SWIPE_DISMISS_DISTANCE = 60;

/** Upward fling speed past which a release dismisses regardless of distance. */
const SWIPE_DISMISS_VELOCITY = 0.5;

/** Drag distance (px) before the gesture is read as deliberate rather than a stray touch. */
const SWIPE_CLAIM_DISTANCE = 8;

/** Where the exit animates to before the height of the surface is known. */
const FALLBACK_EXIT_DISTANCE = 160;

/** React Native's native driver cannot run under react-native-web; transforms there stay on JS. */
const USE_NATIVE_DRIVER = Platform.OS !== 'web';

/**
 * Whether a release should dismiss.
 *
 * Distance or speed, not both: a slow deliberate drag and a quick flick are the
 * same intent expressed differently, and requiring distance from a flick makes
 * the gesture feel unresponsive.
 */
export function shouldDismissSwipe({ dy, vy }: SwipeRelease): boolean {
  return dy < -SWIPE_DISMISS_DISTANCE || vy < -SWIPE_DISMISS_VELOCITY;
}

/**
 * Swipe a surface upward off the screen, then tell the caller it is gone.
 *
 * Built on core `PanResponder` and `Animated` rather than
 * react-native-gesture-handler, which is the preference this repo otherwise
 * states. RNGH gestures only fire inside `GestureHandlerRootView`, and this is
 * used from a banner hosted in a navigator-supplied header, which is exactly the
 * placement that has already cost one surface its gestures here. The responder
 * system carries no such requirement.
 *
 * The gesture claims only a deliberate upward drag, so taps on a call to action
 * and horizontal scrolls still reach their own handlers. Downward drag is
 * clamped out: the surface is anchored to the top and pulling it down looks like
 * a bug.
 *
 * Under reduced motion the surface is dismissed outright rather than slid away.
 * The state change still happens; only the travel is dropped.
 *
 * The two callbacks fire at different moments on purpose. `onDismiss` reports
 * the reader's intent the instant it is committed, so anything that must be
 * recorded survives the surface being torn down mid-flight. `onExited` runs once
 * the surface has actually left, which is where a caller removes it — doing that
 * at commit would unmount the thing being animated.
 */
export function useSwipeUpDismiss({
  onDismiss,
  onExited,
}: {
  onDismiss?: (source: SwipeDismissSource) => void;
  onExited: () => void;
}) {
  const translateY = useRef(new Animated.Value(0)).current;

  /**
   * Driven only by the exit, never by the drag.
   *
   * Deriving it from `translateY` instead meant interpolating over a range fixed
   * before the surface had been measured, so the fade finished wherever that
   * guess happened to land — for a short surface, at half opacity, with the rest
   * of the fade happening as a pop at unmount. Dragging also has no business
   * dimming something the reader may be about to release.
   */
  const opacity = useRef(new Animated.Value(1)).current;

  const heightRef = useRef(FALLBACK_EXIT_DISTANCE);

  /**
   * True from the moment a dismissal is committed until the surface is gone.
   *
   * The responder stays attached for the length of the exit, so without this a
   * second flick reports a second dismissal and starts a second animation on a
   * value the native driver already owns.
   */
  const isExitingRef = useRef(false);

  const prefersReducedMotion = useReducedMotion();

  const prefersReducedMotionRef = useRef(prefersReducedMotion);

  const onDismissRef = useRef(onDismiss);

  const onExitedRef = useRef(onExited);

  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion;
  }, [prefersReducedMotion]);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    onExitedRef.current = onExited;
  }, [onExited]);

  const dismiss = useCallback(
    (source: SwipeDismissSource) => {
      if (isExitingRef.current) {
        return;
      }

      isExitingRef.current = true;

      onDismissRef.current?.(source);

      if (prefersReducedMotionRef.current) {
        onExitedRef.current();

        return;
      }

      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -heightRef.current,
          duration: DURATION.fast,
          useNativeDriver: USE_NATIVE_DRIVER,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: DURATION.fast,
          useNativeDriver: USE_NATIVE_DRIVER,
        }),
      ]).start(({ finished }) => {
        if (finished) {
          onExitedRef.current();
        }
      });
    },
    [translateY, opacity]
  );

  const springBack = useCallback(() => {
    Animated.spring(translateY, {
      toValue: 0,
      useNativeDriver: USE_NATIVE_DRIVER,
    }).start();
  }, [translateY]);

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_event, gesture) =>
        !isExitingRef.current &&
        gesture.dy < -SWIPE_CLAIM_DISTANCE &&
        Math.abs(gesture.dy) > Math.abs(gesture.dx),
      onPanResponderMove: (_event, gesture) => {
        if (isExitingRef.current) {
          return;
        }

        translateY.setValue(Math.min(0, gesture.dy));
      },
      onPanResponderRelease: (_event, gesture) => {
        if (shouldDismissSwipe(gesture)) {
          dismiss('gesture');

          return;
        }

        springBack();
      },
      // A system gesture or a navigation transition can take the responder away
      // mid-drag, and neither release handler runs when it does. Without this the
      // surface is left wherever the finger stopped.
      onPanResponderTerminate: () => {
        if (isExitingRef.current) {
          return;
        }

        springBack();
      },
    })
  ).current;

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    heightRef.current = event.nativeEvent.layout.height || FALLBACK_EXIT_DISTANCE;
  }, []);

  return {
    panHandlers: panResponder.panHandlers,
    animatedStyle: { opacity, transform: [{ translateY }] },
    onLayout,
    /** Dismisses without a gesture, for the accessibility action a swipe cannot serve. */
    dismissByAction: () => dismiss('action'),
  };
}
